/*
  "Load more" pagination (docs/page-specs/collection.md "Grid", docs/04).

  <load-more> wraps the "Showing n of m" line and the Load more link that
  snippets/product-grid.liquid renders under the grid. A click fetches the
  next page of the same section through the Section Rendering API, appends
  its cards to the grid (made lazy, the first row of the page is eager only
  when it is the first thing on screen), replaces this element and the
  crawler pagination with the fetched ones, moves focus to the first new card
  and sets the URL to ?page=n with replaceState. The <a> stays a real link to
  the next page for crawlers and for browsers without JavaScript.

  Back navigation: the browser restores the page from bfcache with the loaded
  pages and scroll position intact where it can. Where it cannot, the page is
  served at ?page=n with only that page's cards, so on load this script reads
  the range it recorded in history.state, fetches the earlier pages in order,
  inserts them before the served page and restores the scroll position saved
  on pagehide.
*/

class LoadMore extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.link = this.querySelector('[data-load-more]');
    if (this.link) this.link.addEventListener('click', (event) => this.onClick(event));
  }

  get grid() {
    return document.getElementById(`product-grid-${this.sectionId}`);
  }

  static pageOf(url) {
    const page = Number(new URL(url, window.location.origin).searchParams.get('page'));
    return page > 0 ? page : 1;
  }

  static sectionUrl(url, sectionId) {
    const next = new URL(url, window.location.origin);
    next.searchParams.set('section_id', sectionId);
    return next;
  }

  static makeLazy(items) {
    items.forEach((item) => {
      item.querySelectorAll('img').forEach((img) => {
        img.setAttribute('loading', 'lazy');
        img.removeAttribute('fetchpriority');
      });
    });
  }

  static fetchItems(url, sectionId) {
    return fetch(LoadMore.sectionUrl(url, sectionId))
      .then((response) => response.text())
      .then((html) => new DOMParser().parseFromString(html, 'text/html'));
  }

  onClick(event) {
    event.preventDefault();
    if (this.link.getAttribute('aria-busy') === 'true') return;
    const grid = this.grid;
    if (!grid) return;

    const href = this.link.href;
    const nextPage = LoadMore.pageOf(href);
    this.link.setAttribute('aria-busy', 'true');
    this.link.classList.add('is-loading');

    LoadMore.fetchItems(href, this.sectionId)
      .then((doc) => {
        const items = Array.from(doc.querySelectorAll(`#product-grid-${this.sectionId} > li`));
        LoadMore.makeLazy(items);
        grid.append(...items);

        const pagination = document.getElementById(`Pagination-${this.sectionId}`);
        const sourcePagination = doc.getElementById(`Pagination-${this.sectionId}`);
        if (pagination && sourcePagination) pagination.replaceWith(sourcePagination);

        const state = history.state || {};
        const loadedFrom = state.loadedFrom || LoadMore.pageOf(window.location.href);
        const url = new URL(href, window.location.origin);
        url.searchParams.delete('section_id');
        history.replaceState({ ...state, loadedFrom, page: nextPage }, '', url);

        const sourceSelf = doc.getElementById(this.id);
        if (sourceSelf) this.replaceWith(sourceSelf);
        else this.remove();

        const firstLink = items[0]?.querySelector('a');
        if (firstLink) firstLink.focus({ preventScroll: true });
      })
      .catch((error) => {
        console.error(error);
        this.link.removeAttribute('aria-busy');
        this.link.classList.remove('is-loading');
      });
  }

  // Rebuild the pages that were loaded before a non-bfcache back navigation.
  static restore() {
    const state = history.state;
    if (!state || !state.loadedFrom || !state.page || state.page <= state.loadedFrom) return;
    const element = document.querySelector('load-more');
    const sectionId = element?.dataset.sectionId;
    const grid = sectionId ? document.getElementById(`product-grid-${sectionId}`) : null;
    if (!grid) return;

    const servedPage = LoadMore.pageOf(window.location.href);
    if (servedPage !== state.page) return;

    const anchor = grid.firstElementChild;
    const pages = [];
    for (let page = state.loadedFrom; page < state.page; page += 1) pages.push(page);

    pages
      .reduce(
        (chain, page) =>
          chain.then(() => {
            const url = new URL(window.location.href);
            url.searchParams.set('page', String(page));
            return LoadMore.fetchItems(url.href, sectionId).then((doc) => {
              const items = Array.from(doc.querySelectorAll(`#product-grid-${sectionId} > li`));
              LoadMore.makeLazy(items);
              if (anchor) anchor.before(...items);
              else grid.append(...items);
            });
          }),
        Promise.resolve(),
      )
      .then(() => {
        if (typeof state.scrollY === 'number') window.scrollTo(0, state.scrollY);
      })
      .catch((error) => console.error(error));
  }
}

customElements.define('load-more', LoadMore);

window.addEventListener('pagehide', () => {
  const state = history.state;
  if (state && state.loadedFrom) history.replaceState({ ...state, scrollY: window.scrollY }, '');
});

window.addEventListener('pageshow', (event) => {
  if (!event.persisted) LoadMore.restore();
});
